import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  ListItem,
  ListItemText,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
  Card,
  CardContent,
  Grid,
  Collapse,
  Tooltip,
  TableFooter,
  Autocomplete,
} from "@mui/material";
import {
  Search,
  Close,
  Person,
  Payment,
  Receipt,
  ArrowDownward,
  ArrowUpward,
  AccountBalance,
  CheckCircle,
  Warning,
  ExpandMore,
  ExpandLess,
  ShoppingCart,
} from "@mui/icons-material";
import { formatCurrencyN } from "../../../common/utils_t";
import {
  createCustomerPayment_giftcard_crypto,
  getCustomersOrders_giftcard_crypto,
  searchCustomers,
  getAllCustomers2,
} from "../../../api/customers/customers";
import { IonApp, IonButton, IonContent, IonHeader, IonPage, IonToolbar, useIonViewWillEnter } from "@ionic/react";
import ReceiptModal_giftcard_crypto from "../../../components/sales/ReceiptModal_giftcard_crypto";
import { MenuButton } from "../../../components/Menu";

interface OrderItem {
  product_id: string;
  quantity: string;
  product_name: string;
  price: string;
  total_amount: string;
  item_status: string;
  card_amount?: string | null;
  load_type?: string | null;
  card_rate?: string | null;
  naira_rate?: string | null;
  internal_rate?: string | null;
  
  username?: string | null;
  processor_type?: string | null;
  external_processor?: string | null;
  
  customer_bank_code?: string | null;
  customer_account_number?: string | null;
  customer_account_name?: string | null;
  
  otp_status: string | null;
  last_updated: string; // datetime
  deleted_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  
  product_id_type: string;
  product_id_single: string;
  rmbValue: string;
  commission: string;
  proof_of_payment: string;
  costPrice: string;
  sellingPrice: string;
}

interface Transaction {
  id: number; // primary key
  business_id: string;
  branch_id: string | null;
  
  order_id: string;
  order_code: string;
  order_date: string; // datetime
  active_status: "COMPLETED" | "PENDING" | "CANCELLED" | "REVERSED"; // from enum
  payment_status: 0 | 1 | 2; // 0 = unpaid, 1 = paid, 2 = partial
  
  total_price: string;
  payable_amount?: string | null;
  amount_paid?: string | null;
  balance_due?: number;
  
  mode_of_payment: string | null;
  order_by: string;
  approved_by?: string | null;
  
  member_id: string;
  member_name: string; // added from join
  customer_name?: string | null;
  customer_email?: string | null;
  customer_phone?: string | null;
  
  request_id?: string | null;
  card_amount?: string | null;
  load_type?: string | null;
  card_rate?: string | null;
  naira_rate?: string | null;
  internal_rate?: string | null;
  
  username?: string | null;
  processor_type?: string | null;
  external_processor?: string | null;
  
  customer_bank_code?: string | null;
  customer_account_number?: string | null;
  customer_account_name?: string | null;
  bank_name?: string | null;
  
  otp_status: string | null;
  last_updated: string; // datetime
  deleted_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  
  product_id_type: string;
  product_id_single: string;
  rmbValue: string;
  commission: string;
  proof_of_payment: string;
  costPrice: string;
  sellingPrice: string;
  cryptoType?: string | null;
  cryptoAmount?: string | null;
  staff_name?: string | null;
  cardUnit?: string | null;
  cardValue?: string | null;
  unitPrice?: string | null;

  items: OrderItem[];
}

interface Customer {
  id: string;
  user_id: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  grade_letter: string;
}

const CustomerAccountPage_giftcard_crypto_fdesk: React.FC = () => {
  const location = useLocation<{ customer: Customer }>();
  const [allCustomers, setAllCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState({
    customers: false,
    transactions: false,
    payment: false,
  });

  // Payment dialog states
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState<string[]>([]);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [selectAll, setSelectAll] = useState(false);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [totalCardUnit, setTotalCardUnit] = useState(0);
  const [totalCardValue, setTotalCardValue] = useState(0);
  const [totalUnitPrice, setTotalUnitPrice] = useState(0);

  useEffect(() => {
    console.log(location.state)
    if (location.state && location.state.customer) {
      handleCustomerSelect(location.state.customer);
    }
  }, [location.state]);

  // Fetch all customers on component mount
  useEffect(() => {
    fetchAllCustomers();
  }, []);

  const fetchAllCustomers = async () => {
    try {
      setLoading((prev) => ({ ...prev, customers: true }));
      const response = await getAllCustomers2();
      setAllCustomers(response.data);
    } catch (error) {
      console.error("Error fetching all customers:", error);
    } finally {
      setLoading((prev) => ({ ...prev, customers: false }));
    }
  };

  // Calculate totals based on actual data structure
  const unpaidTransactions = transactions.filter(
    (txn) => txn.payment_status == '0' || txn.payment_status == "2"
  );

  const selectedTransactionTotal = unpaidTransactions
    .filter((txn) => selectedTransactions.includes(txn.order_id))
    .reduce((sum, txn) => {
      const amount = txn.total_price || txn.payable_amount || "0";
      const balanceDue = txn.balance_due || amount;
      return sum + Number(balanceDue);
    }, 0);

  // Calculate total cost price and commission for selected transactions
  const selectedTransactionsData = unpaidTransactions.filter(txn => 
    selectedTransactions.includes(txn.order_id)
  );
  
  const totalCostPrice = selectedTransactionsData.reduce((sum, txn) => {
    return sum + (parseFloat(txn.costPrice) || 0);
  }, 0);
  
  const totalCommission = selectedTransactionsData.reduce((sum, txn) => {
    return sum + (parseFloat(txn.commission) || 0);
  }, 0);

  const totalOutstanding = unpaidTransactions.reduce((sum, txn) => {
    const amount = parseFloat(txn.total_price);
    const balanceDue = txn.balance_due || amount;
    return sum + balanceDue;
  }, 0);

  // Check if any selected transaction has proof_of_payment
  const hasProofOfPayment = selectedTransactions.some(transactionId => {
    const transaction = transactions.find(txn => txn.order_id === transactionId);
    return transaction && transaction.proof_of_payment && (transaction.proof_of_payment.trim() !== "noImage_ep" && transaction.proof_of_payment.trim() !== "noImage" && transaction.proof_of_payment.trim() !== "noImage1");
  });

  const isAnyGiftcardSelected = selectedTransactions.some(transactionId => {
    const transaction = transactions.find(txn => txn.order_id === transactionId);
    return transaction && transaction.product_id_type === 'GiftCard';
  });

  const getPaymentStatusLabel = (status: string | number) => {
    const code = Number(status);
    switch (code) {
      case 0:
        return "Unpaid";
      case 1:
        return "Paid";
      case 2:
        return "Partial";
      default:
        return "Unknown";
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case "0":
        return "error";
      case "1":
        return "success";
      case "2":
        return "warning";
      default:
        return "default";
    }
  };

  const getOrderStatusColor = (status: string) => {
    switch (status) {
      case "PENDING":
        return "success";
      case "PENDING":
        return "warning";
      case "CANCELLED":
        return "error";
      default:
        return "default";
    }
  };

  const toggleRowExpansion = (orderId: string) => {
    setExpandedRows((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };

  const fetchCustomerTransactions = async (customerId: string) => {
    try {
      setLoading((prev) => ({ ...prev, transactions: true }));
      const response = await getCustomersOrders_giftcard_crypto(customerId);

      // Process transactions to calculate balance due for partial payments
      const processedTransactions = response.data.orders.map((txn: Transaction) => {
        const totalAmount = parseFloat(txn.payable_amount || txn.total_price);
        let balanceDue = Number(txn.balance_due);

        // If partial payment, calculate remaining balance
        if (txn.payment_status == "2") {
          balanceDue = totalAmount * 0.5;
        } else if (txn.payment_status == "1") {
          balanceDue = 0;
        }

        return {
          ...txn,
          balance_due: balanceDue,
        };
      });
      setTransactions(processedTransactions);
      setBalance(Number(response.data.balance));
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setLoading((prev) => ({ ...prev, transactions: false }));
    }
  };

  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer);
    setSelectedTransactions([]);
    setPaymentAmount(0);
    setExpandedRows([]);
    fetchCustomerTransactions(customer.user_id);
  };

  const handleTransactionSelect = (transactionId: string) => {
    setSelectedTransactions((prev) => {
      const newSelected = prev.includes(transactionId)
        ? prev.filter((id) => id !== transactionId)
        : [...prev, transactionId];

      // Update payment amount to match selected transactions
      const newTotal = unpaidTransactions
        .filter((txn) => newSelected.includes(txn.order_id))
        .reduce(
          (sum, txn) => sum + (txn.balance_due || parseFloat(txn.total_price)),
          0
        );

      setPaymentAmount(newTotal);
      return newSelected;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedTransactions([]);
      setPaymentAmount(0);
    } else {
      const allUnpaidIds = unpaidTransactions.map((txn) => txn.order_id);
      setSelectedTransactions(allUnpaidIds);
      setPaymentAmount(totalOutstanding);
    }
    setSelectAll(!selectAll);
  };

  const handlePaymentAmountChange = (value: number) => {
    setPaymentAmount(Math.abs(value) || 0);
  };

  const getPaymentType = () => {
    if (paymentAmount === selectedTransactionTotal) return "exact";
    if (paymentAmount > selectedTransactionTotal) return "overpayment";
    if (paymentAmount < selectedTransactionTotal) return "partial";
    return "exact";
  };

  const handlePaymentSubmit = async () => {
    if (!selectedCustomer || selectedTransactions.length === 0 || paymentAmount <= 0) return;

    try {
      setLoading((prev) => ({ ...prev, payment: true }));

      const paymentType = getPaymentType();

      // Create payment data (removed posting_mode)
      const data = {
        customer_id: selectedCustomer.user_id,
        order_ids: selectedTransactions,
        amount: paymentAmount,
        payment_method: paymentMethod,
        payment_type: paymentType,
        notes: paymentNotes || `Payment for ${selectedTransactions.length} order(s)`,
        // posting_mode removed
      };

      //alert('ererererer');;
      const response = await createCustomerPayment_giftcard_crypto(data);
console.log(response);

      if (response.status === false) {
        
        alert(response.message);

        return;
      }
      // Generate receipt with all payment details
      handleGenerateReceipt(data);
      
      // Refresh transactions and balance
      await fetchCustomerTransactions(selectedCustomer.user_id);

      // Reset payment dialog
      setShowPaymentDialog(false);
      setSelectedTransactions([]);
      setPaymentAmount(0);
      setPaymentNotes("");
      setSelectAll(false);
    } catch (error) {
      console.error("Error creating payment:", error);
    } finally {
      setLoading((prev) => ({ ...prev, payment: false }));
    }
  };

  useEffect(() => {
    setSelectAll(
      selectedTransactions.length === unpaidTransactions.length &&
        unpaidTransactions.length > 0
    );
  }, [selectedTransactions, unpaidTransactions]);

  // Auto-select payment method based on proof_of_payment
  useEffect(() => {
    if (showPaymentDialog && selectedTransactions.length > 0) {
      if (hasProofOfPayment) {
        setPaymentMethod("manual_payment");
      } else {
        setPaymentMethod("auto_payment");
      }
    }
  }, [showPaymentDialog, selectedTransactions, hasProofOfPayment]);

  useEffect(() => {
    const selected = unpaidTransactions.filter(txn => 
      selectedTransactions.includes(txn.order_id)
    );
    const unitTotal = selected.reduce((sum, txn) => sum + (parseFloat(txn.cardUnit) || 0), 0);
    const valueTotal = selected.reduce((sum, txn) => sum + (parseFloat(txn.cardValue) || 0), 0);
    const unitPriceTotal = selected.reduce((sum, txn) => sum + (parseFloat(txn.unitPrice) || 0), 0);

    setTotalCardUnit(unitTotal);
    setTotalCardValue(valueTotal);
    setTotalUnitPrice(unitPriceTotal);
  }, [selectedTransactions, unpaidTransactions]);

  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptData, setReceiptData] = useState<any>(null);

  const handleGenerateReceipt = (paymentData: any) => {
    if (!selectedCustomer) return;

    const paidTransactions = transactions.filter(txn => 
      paymentData.order_ids.includes(txn.order_id)
    );

    const totalAmount = paidTransactions.reduce((sum, txn) => {
      return sum + txn.items.reduce((txnSum, item) => {
        return txnSum + (parseFloat(item.total_amount) || 0);
      }, 0);
    }, 0);

    const initialBalanceDue = paidTransactions.reduce((sum, txn) => {
      return sum + (txn.balance_due || 0);
    }, 0);

    const paidAmount = paymentData.amount;
    const actualBalanceDue = Math.max(0, initialBalanceDue - paidAmount);

    const receiptItems = paidTransactions.map(txn => ({
      orderID: txn.order_id,
      productName: txn.product_name || 'Unknown Product',
      quantity: txn.card_amount || '0',
      unitPrice: parseFloat(txn.sellingPrice) || 0,
      amount: parseFloat(txn.sellingPrice) || 0,
      status: txn.order_status || 'COMPLETED',
      product_id_type: txn.product_id_type || 'N/Ap',
      cryptoType: txn.cryptoType || 'N/Ap',
      cryptoAmount: txn.cryptoAmount || 'N/Ap',
      giftcardType: txn.giftcardType || 'N/Ap',
      cardValue: txn.cardValue || 'N/Ap',
    }));

    setReceiptData({
      customer: {
        name: `${selectedCustomer.firstName} ${selectedCustomer.lastName}`,
        phone: selectedCustomer.phone || 'N/A',
        email: selectedCustomer.email || 'N/A',
      },
      order: {
        id: paidTransactions.length === 1 
          ? paidTransactions[0].order_code 
          : `Multiple Orders (${paidTransactions.length})`,
        date: paidTransactions.length === 1 
          ? paidTransactions[0].order_date 
          : new Date().toISOString(),
        status: paidTransactions.length === 1 
          ? paidTransactions[0].order_status 
          : 'COMPLETED',
        paymentStatus: '1',
        paymentMethod: paymentData.payment_method || 'cash',
        customer_account_name: paidTransactions[0].customer_account_name || 'N/A',
        customer_account_number: paidTransactions[0].customer_account_number || 'N/A',
        bank_name: paidTransactions[0].bank_name || 'N/A'
      },
      items: receiptItems,
      summary: {
        totalAmount: paidTransactions[0].payable_amount,
        paidAmount,
        balanceDue: actualBalanceDue
      },
      payment: {
        amount: paidAmount,
        method: paymentData.payment_method || 'cash',
        date: new Date().toISOString(),
        notes: paymentData.notes || `Payment for ${paidTransactions.length} order(s)`
      }
    });
    setReceiptOpen(true);
  };

  const handlePrintReceipt = (transaction: Transaction) => {
    if (!selectedCustomer) return;

    const receiptItems = [{
      orderID: transaction.order_id,
      productName: transaction.product_name || 'Unknown Product',
      quantity: transaction.card_amount || '0',
      unitPrice: parseFloat(transaction.sellingPrice) || 0,
      amount: parseFloat(transaction.sellingPrice) || 0,
      status: transaction.order_status || 'COMPLETED',
      product_id_type: transaction.product_id_type || 'N/Ak',
      cryptoType: transaction.cryptoType || 'N/Ak',
      cryptoAmount: transaction.cryptoAmount || 'N/Ak',
      giftcardType: transaction.giftcardType || 'N/Ap',
      cardValue: transaction.cardValue || 'N/Ap',
    }];

    const totalAmount = parseFloat(transaction.payable_amount || transaction.total_price);
    const paidAmount = totalAmount - (transaction.balance_due || 0);

    setReceiptData({
      customer: {
        name: `${selectedCustomer.firstName} ${selectedCustomer.lastName}`,
        phone: selectedCustomer.phone,
        email: selectedCustomer.email
      },
      order: {
        id: transaction.order_code,
        date: transaction.order_date,
        status: transaction.order_status,
        paymentStatus: getPaymentStatusLabel(transaction.payment_status),
        paymentMethod: transaction.mode_of_payment || 'cash',
        customer_account_name: transaction.customer_account_name || 'N/A',
        customer_account_number: transaction.customer_account_number || 'N/A',
        bank_name: transaction.bank_name || 'N/A'
      },
      items: receiptItems,
      summary: {
        totalAmount,
        paidAmount,
        balanceDue: transaction.balance_due || 0
      }
    });

    setReceiptOpen(true);
  };

  const totalPayableAmount = transactions.reduce(
    (sum, txn) => sum + parseFloat(txn.payable_amount || txn.total_price || '0'),
    0
  );

  const totalBalanceDue = transactions.reduce(
    (sum, txn) => sum + (txn.balance_due || 0),
    0
  );

  return (
    <IonPage className="pushDown">
               <MenuButton/>
     
      <IonContent>
        <Box sx={{ p: 3 }}>
          <Typography variant="h4" gutterBottom>
            Customer Transactions For GiftCard and Crypto
          </Typography>

          {/* Customer Selection - Updated to Autocomplete */}
          <Box sx={{ mb: 4 }}>
            <Autocomplete
              sx={{ width: '100%' }}
              options={allCustomers}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.phone})`}
              value={selectedCustomer}
              onChange={(event, newValue) => {
                if (newValue) {
                  handleCustomerSelect(newValue);
                } else {
                  setSelectedCustomer(null);
                  setTransactions([]);
                  setSelectedTransactions([]);
                }
              }}
              loading={loading.customers}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select a customer..."
                  variant="outlined"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {loading.customers ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>

          {/* Customer Account Section */}
          {selectedCustomer && (
            <Box>
              {/* Customer Info Header */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar sx={{ width: 56, height: 56, mr: 2 }}>
                      <Person fontSize="large" />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h5">
                        {selectedCustomer.firstName} {selectedCustomer.lastName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedCustomer.phone} • {selectedCustomer.email}
                      </Typography>
                    </Box>
                    <IconButton onClick={() => {
                      setSelectedCustomer(null);
                      setTransactions([]);
                      setSelectedTransactions([]);
                    }}>
                      <Close />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>

              {/* Balance and Payment Summary */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ bgcolor: "warning.light" }}>
                    <CardContent sx={{ textAlign: "center" }}>
                      <Warning sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h6">Outstanding</Typography>
                      <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                        {formatCurrencyN(totalOutstanding)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent sx={{ textAlign: "center", bgcolor: "success.light" }}>
                      <Receipt sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h6">Total Orders</Typography>
                      <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                        {transactions?.length}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Payment Actions */}
              {unpaidTransactions.length > 0 && (
                <Box sx={{ mb: 3, display: "flex", gap: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Payment />}
                    onClick={() => setShowPaymentDialog(true)}
                    disabled={selectedTransactions.length === 0}
                  >
                    Process Payment ({selectedTransactions.length} selected)
                  </Button>
                </Box>
              )}

              {/* Orders Table */}
              <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                Order History
              </Typography>

              {unpaidTransactions.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectAll}
                        onChange={handleSelectAll}
                        indeterminate={
                          selectedTransactions.length > 0 && !selectAll
                        }
                      />
                    }
                    label={`Select All Unpaid Orders (${formatCurrencyN(
                      totalOutstanding
                    )})`}
                  />
                </Box>
              )}

              {loading.transactions ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : transactions.length > 0 ? (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">Select</TableCell>
                        <TableCell>Order Code</TableCell>
                        <TableCell>Service</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Staff Name</TableCell>
                        <TableCell align="right">Cost Price</TableCell>
                        <TableCell align="right">Selling Price</TableCell>
                        <TableCell align="right">Payout Due</TableCell>
                        <TableCell align="right">Commission</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Card Unit</TableCell>
                        <TableCell align="right">Total Card Value</TableCell>
                        <TableCell>Receipts</TableCell>
                        <TableCell>Order Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions.map((txn) => {
                        const isUnpaid = txn.payment_status == '0' || txn.payment_status == "2";
                        const isSelected = selectedTransactions.includes(txn.order_id);
                        const isExpanded = expandedRows.includes(txn.order_id);

                        return (
                          <React.Fragment key={txn.order_id}>
                            <TableRow
                              sx={{
                                bgcolor: isSelected
                                  ? "action.selected"
                                  : "inherit",
                                "&:hover": { bgcolor: "action.hover" },
                              }}
                            >
                              <TableCell padding="checkbox">
                                {isUnpaid && (
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={() =>
                                      handleTransactionSelect(txn.order_id)
                                    }
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: "bold" }}
                                >
                                  {txn.order_code}
                                </Typography>
                              </TableCell>

                              <TableCell>{txn.product_id_type} 
                                
                              {/* */}  ({txn.cryptoType ? (
   (txn.cryptoType)

                                ):(txn.giftcardType)})
                             
                                
                                
                                
                                </TableCell>


                              <TableCell>{txn.order_date}</TableCell>
                              <TableCell>{txn.staff_name}</TableCell>
                              <TableCell align="right">
                                {formatCurrencyN(parseFloat(txn.costPrice))}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ fontWeight: "bold" }}
                              >
                                {formatCurrencyN(
                                  parseFloat(txn.payable_amount)
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{
                                color: (txn.balance_due || 0) > 0 ? 'error.main' : 'success.main',
                                fontWeight: 'bold'
                              }}>
                                {formatCurrencyN(Number(txn.balance_due) || 0)}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrencyN(Number(txn.commission) || 0)}
                              </TableCell>


                              {txn.product_id_type === 'GiftCard' ? ( 
<>
  <TableCell align="right" sx={{
                                color: (txn.unitPrice || 0) > 0 ? 'error.main' : 'success.main',
                                fontWeight: 'bold'
                              }}>
                                {formatCurrencyN(Number(txn.unitPrice) || 0)}
                              </TableCell>

                                <TableCell align="right" sx={{
                                color: (txn.balance_due || 0) > 0 ? 'error.main' : 'success.main',
                                fontWeight: 'bold'
                              }}>
                                {(Number(txn.cardUnit) || 0)}
                              </TableCell>

                                <TableCell align="right" sx={{
                                color: (txn.balance_due || 0) > 0 ? 'error.main' : 'success.main',
                                fontWeight: 'bold'
                              }}>
                                {(Number(txn.cardValue) || 0)}
                              </TableCell>
                          </>
):(
<>
                          <TableCell align="right">-</TableCell>
                          <TableCell align="right">-</TableCell>
                          <TableCell align="right">-</TableCell>
</>
)}
                              <TableCell>
                                <Tooltip title="Print receipt">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handlePrintReceipt(txn)}
                                    color="primary"
                                  > 
                                    View
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={txn.balance_due > 0 ? "PENDING" : "COMPLETED"}
                                  size="small"
                                  color={
                                    getOrderStatusColor(txn.order_status) as any
                                  }
                                />
                              </TableCell>
                            </TableRow>

                            {/* Expandable row for order items */}
                            <TableRow>
                              <TableCell
                                style={{ paddingBottom: 0, paddingTop: 0 }}
                                colSpan={14}
                              >
                                <Collapse
                                  in={isExpanded}
                                  timeout="auto"
                                  unmountOnExit
                                >
                                  <Box sx={{ margin: 1 }}>
                                    <Typography
                                      variant="h6"
                                      gutterBottom
                                      component="div"
                                    >
                                      Order Items
                                    </Typography>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Product</TableCell>
                                          <TableCell align="right">
                                            Quantity
                                          </TableCell>
                                          <TableCell align="right">
                                            Unit Price
                                          </TableCell>
                                          <TableCell align="right">
                                            Total
                                          </TableCell>
                                          <TableCell>Status</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {txn.items.map((item, index) => (
                                          <TableRow
                                            key={`${txn.order_id}-${index}`}
                                          >
                                            <TableCell>
                                              {item.product_name}
                                            </TableCell>
                                            <TableCell align="right">
                                              {item.quantity}
                                            </TableCell>
                                            <TableCell align="right">
                                              {formatCurrencyN(
                                                parseFloat(item.price)
                                              )}
                                            </TableCell>
                                            <TableCell align="right">
                                              {formatCurrencyN(
                                                parseFloat(item.total_amount)
                                              )}
                                            </TableCell>
                                            <TableCell>
                                              <Chip
                                                label={item.item_status}
                                                size="small"
                                                color={
                                                  item.item_status ===
                                                  "COMPLETED"
                                                    ? "success"
                                                    : "default"
                                                }
                                              />
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell colSpan={5} align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                          Totals
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                          {formatCurrencyN(totalPayableAmount)}
                        </TableCell>
                        <TableCell colSpan={8} />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
              ) : (
                <Paper sx={{ p: 3, textAlign: "center" }}>
                  <Typography variant="body1" color="text.secondary">
                    No orders found for this customer
                  </Typography>
                </Paper>
              )}
            </Box>
          )}

          {/* Payment Dialog */}
          <Dialog
            open={showPaymentDialog}
            onClose={() => setShowPaymentDialog(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              Process Payment 
              <IconButton
                sx={{ position: "absolute", right: 8, top: 8 }}
                onClick={() => setShowPaymentDialog(false)}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    Customer: {selectedCustomer?.firstName}{" "}
                    {selectedCustomer?.lastName}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Selected Orders: {selectedTransactions.length}
                  </Typography>

              {/* Cost Price and Commission Summary */}
                  {selectedTransactions.length > 0 && (
                    <Box sx={{ mt: 0, p: 0, backgroundColor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Cost Summary:
                      </Typography>
                           
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Total Selling Price:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                          {formatCurrencyN(selectedTransactionTotal)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Total Cost Price:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                          {formatCurrencyN(totalCostPrice)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Total Commission:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                          {formatCurrencyN(totalCommission)}
                        </Typography>
                      </Box>
 
{isAnyGiftcardSelected && (
  <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Total Card Unit:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {totalCardUnit}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Total Card Value:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {(totalCardValue)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Total Unit Price:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {formatCurrencyN(totalUnitPrice)}
                        </Typography>
                      </Box>
  </>
)}

                    </Box>
                  )}

                  {/* Proof of Payment Alert */}
                  {hasProofOfPayment && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Proof of payment detected - Manual payment method selected automatically
                    </Alert>
                  )}

                  {/* Selected Orders List */}
                  <Box sx={{ mt: 2, maxHeight: 200, overflow: "auto" }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Selected Orders:
                    </Typography>
                    {unpaidTransactions
                      .filter((txn) =>
                        selectedTransactions.includes(txn.order_id)
                      )
                      .map((txn) => (
                        <Box
                          key={txn.order_id}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            py: 0.5,
                            borderBottom: '1px solid',
                            borderColor: 'divider'
                          }}
                        >
                          <Box>
                            <Typography variant="body2">
                              {txn.order_code}
                              {(txn.proof_of_payment.trim() !== "noImage_ep" && txn.proof_of_payment.trim() !== "noImage" && txn.proof_of_payment.trim() !== "noImage1") && (
                                <Chip
                                  label="Has Proof"
                                  size="small"
                                  color="success"
                                  sx={{ ml: 1 }}
                                />
                              )}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {/* Optional date or status here */}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "bold" }}
                          >
                            {formatCurrencyN(txn.balance_due)}
                          </Typography>
                        </Box>
                      ))}
                  </Box>

                  {/* Account Details Section */}
                  {selectedTransactions.length > 0 && (
                    <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        Payout Account Details
                      </Typography>
                      
                      {(() => {
                        // Get the first selected transaction for account details
                        const firstSelectedTransaction = unpaidTransactions.find(txn => 
                          selectedTransactions.includes(txn.order_id)
                        );
                        
                        if (firstSelectedTransaction) {
                          const hasAccountDetails = 
                            firstSelectedTransaction.customer_account_name || 
                            firstSelectedTransaction.customer_account_number || 
                            firstSelectedTransaction.bank_name;
                          
                          if (hasAccountDetails) {
                            return (
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    Account Name:
                                  </Typography>
                                  <Typography variant="body2">
                                    {firstSelectedTransaction.customer_account_name || 'N/A'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    Account Number:
                                  </Typography>
                                  <Typography variant="body2">
                                    {firstSelectedTransaction.customer_account_number || 'N/A'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    Bank Name:
                                  </Typography>
                                  <Typography variant="body2">
                                    {firstSelectedTransaction.bank_name || 'N/A'}
                                  </Typography>
                                </Grid>
                              </Grid>
                            );
                          } else {
                            return (
                              <Alert severity="warning">
                                No account details found for the selected transaction(s)
                              </Alert>
                            );
                          }
                        }
                        return null;
                      })()}
                    </Box>
                  )}

                  {/* Payment Type Alert */}
                  {paymentAmount > 0 && (
                    <Alert
                      severity={
                        getPaymentType() === "exact"
                          ? "success"
                          : getPaymentType() === "overpayment"
                          ? "info"
                          : "warning"
                      }
                      sx={{ mt: 2 }}
                    >
                      {getPaymentType() === "exact" &&
                        "Exact payment - all selected orders will be marked as paid"}
                      {getPaymentType() === "overpayment" &&
                        `Overpayment of ${formatCurrencyN(
                          paymentAmount - selectedTransactionTotal
                        )} - excess will be added to account credit`}
                      {getPaymentType() === "partial" &&
                        `Partial payment - ${formatCurrencyN(
                          selectedTransactionTotal - paymentAmount
                        )} will remain outstanding`}
                    </Alert>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Payment Amount"
                    type="number"
                    disabled={true}
                    value={paymentAmount}
                    onChange={(e) =>
                      handlePaymentAmountChange(parseFloat(e.target.value))
                    }
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">₦</InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    select
                    label="Payment Method"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    sx={{ mb: 2 }}
                    disabled={hasProofOfPayment}
                  >
                    <MenuItem value="auto_payment">Auto Bank Payment</MenuItem>
                    {hasProofOfPayment && (
                      <MenuItem value="manual_payment">Manual Bank Payment</MenuItem>
                    )}
                  </TextField>

                  {hasProofOfPayment && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Manual payment method is required when proof of payment exists in the orders
                    </Alert>
                  )}

                  <TextField
                    fullWidth
                    label="Payment Notes"
                    multiline
                    rows={4}
                    value={paymentNotes}
                    onChange={(e) => setPaymentNotes(e.target.value)}
                    placeholder="Optional payment notes..."
                    sx={{ mb: 2 }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowPaymentDialog(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handlePaymentSubmit}
                disabled={
                 !paymentMethod || !paymentAmount ||
                  selectedTransactions.length === 0 ||
                  loading.payment
                }
                startIcon={
                  loading.payment ? <CircularProgress size={20} /> : <Receipt />
                }
              >
                Process Payment ({formatCurrencyN(paymentAmount)})
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </IonContent>

      {receiptData && (
        <ReceiptModal_giftcard_crypto
          open={receiptOpen}
          onClose={() => setReceiptOpen(false)}
          receiptData={receiptData}
        />
      )}
    </IonPage>
  );
};

export default CustomerAccountPage_giftcard_crypto_fdesk;